import React, { useEffect, useState } from 'react';
import { Share } from 'react-native';
import { Modal, Button, VStack, HStack, Box, Text, Spinner } from 'native-base';
import QRCode from 'react-native-qrcode-svg';
import { getText } from 'src/utils/i18n';

interface ShowTransactionModalProps {
    isOpen: boolean;
    onClose: () => void;
    jobId?: string;
    txHash?: string;
}

export default function ShowTransactionModal({ isOpen, onClose, jobId, txHash }: ShowTransactionModalProps) {
    const [qrCodeLoading, setQrCodeLoading] = useState(true);

    useEffect(() => {
        if (isOpen) {
            setQrCodeLoading(true);
            setTimeout(() => {
                setQrCodeLoading(false);
            }, 500);
        }
    }, [isOpen]);

    const getTransactionUrl = () => {
        if (txHash) {
            return `https://hashscan.io/testnet/transaction/${txHash}`;
        }
        return '';
    };

    const handleShareTransaction = async () => {
        const url = getTransactionUrl();
        if (!url || !jobId || !txHash) return;

        try {
            await Share.share({
                message: `Job ID: ${jobId}\nTransaction Hash: ${txHash}\nView on Blockchain: ${url}`,
                url: url,
                title: 'Share Transaction'
            });
        } catch (error) {
            console.error('Error sharing:', error);
        }
    };

    return (
        <Modal 
            size="lg"
            isOpen={isOpen} 
            onClose={onClose}>
            <Modal.Content maxWidth="400px">
                <Modal.CloseButton />
                <Modal.Header>{getText('shareTransaction')}</Modal.Header>
                <Modal.Body>
                    <VStack space={4} alignItems="center" py={2}>
                        {qrCodeLoading ? (
                            <Box h="250px" justifyContent="center" alignItems="center">
                                <Spinner size="lg" color="blue.500" />
                                <Text mt={4} color="gray.600">{getText('loadingQrCode')}</Text>
                            </Box>
                        ) : (
                            jobId && txHash && (
                                <>
                                    <Box bg="white" p={4} borderRadius="md" borderWidth={1} borderColor="gray.200">
                                        <QRCode
                                            value={getTransactionUrl()}
                                            size={200}
                                        />
                                    </Box>
                                    <VStack space={2} width="100%" px={2}>
                                        <VStack space={1}>
                                            <Text fontWeight="bold" color="gray.600" fontSize="sm">{getText('jobIdLabel')}:</Text>
                                            <Text fontSize="xs" color="gray.800" numberOfLines={2} ellipsizeMode="middle">{jobId}</Text>
                                        </VStack>
                                        <VStack space={1}>
                                            <Text fontWeight="bold" color="gray.600" fontSize="sm">{getText('transactionHashLabel')}:</Text>
                                            <Text fontSize="xs" color="gray.800" numberOfLines={2} ellipsizeMode="middle">{txHash}</Text>
                                        </VStack>
                                    </VStack>
                                </>
                            )
                        )}
                    </VStack>
                </Modal.Body>
                <Modal.Footer>
                    <Button.Group space={2}>
                        <Button 
                            variant="ghost" 
                            colorScheme="blueGray"
                            onPress={onClose}
                        >
                            {getText('cancel')}
                        </Button>
                        <Button 
                            onPress={handleShareTransaction}
                            isDisabled={qrCodeLoading}
                        >
                            {getText('share')}
                        </Button>
                    </Button.Group>
                </Modal.Footer>
            </Modal.Content>
        </Modal>
    );
}
